import React, { useEffect, useState } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import classNames from 'classnames';

import Icons from 'assets/icons';

import { useGoalForm } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/context/context';
import useInitialize from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/goal-form/hooks/useInitialize';

import Select from 'components/partials/select/select';
import RadioInput from 'components/partials/radio-input/radio-input';
import Textarea from 'components/partials/textarea/textarea';
import FormattedTextInput from 'components/partials/formatted-input/formatted-input';
import DetailFields from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/goal-form/goal-details/goal-details';
import Button from 'components/partials/button/button';
import OtherGoalForm from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/goal-form/other-goal-form/other-goal-form';
import GeneratingTextarea from 'components/partials/generating-textarea/generating-textarea';

import { prepareGoalPayload } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goals-manager.helper';

import { schema } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-configuration/goals-configuration';

import {
  getValueLabel,
  validateValue,
} from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/goal-form/goal-form.helpers';

import { TYPES_WITHOUT_SUFFIXES } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-tab.constants';
import { getTestId } from 'helpers/test-helpers';

import { generateGoalSummary } from 'api/generated/goal-summary-controller/goal-summary-controller';
import type { GoalSummaryRequest } from 'api/generated/schemas';

import type {
  Goal,
  SubTypeOption,
  FunnelMetricOption,
} from 'containers/admin/clients/client/tabs-components/goals-tab/types';

interface RadioOption {
  label: string;
  value: 'Increase' | 'Maintain' | 'Decrease';
  colorClass: string;
}

const radioOptions: RadioOption[] = [
  { label: 'Increase', value: 'Increase', colorClass: 'text-success-700' },
  { label: 'Maintain', value: 'Maintain', colorClass: 'text-gray-600' },
  { label: 'Decrease', value: 'Decrease', colorClass: 'text-critical-600' },
];

interface GoalFormProps {
  type: string;
  category: string;
  isEditing?: boolean;
  data?: Goal;
  isCreation: boolean;
  userId: string | undefined;
  enteringClassYear: number;
}

const GoalForm = ({
  type,
  category,
  isEditing,
  data,
  isCreation,
  userId,
  enteringClassYear,
}: GoalFormProps): React.JSX.Element => {
  const [generationLoading, setGenerationLoading] = useState<boolean>(false);
  const { control, setValue, reset, clearErrors, loading, getValues } =
    useGoalForm();

  const parsedType = type === 'Academic Quality' ? type.replace(' ', '') : type;

  const [valueType, setValueType] = useState<string | undefined>(
    schema[parsedType]?.valueType
  );
  const isDisabled = (Boolean(data) && !isEditing) || generationLoading;

  const initializeFormValues = useInitialize(type, setValue);

  const {
    fields: detailFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'details',
  });

  const generateSummary = async (): Promise<void> => {
    if (!userId) return;

    setGenerationLoading(true);

    const formData = getValues();

    const payload = prepareGoalPayload({
      userId,
      clientId: undefined,
      data: { ...(data ?? {}), type, category, enteringClassYear } as never,
      formData: formData as never,
    });

    try {
      const summaryPayload = payload.goalData as GoalSummaryRequest;

      const result = await generateGoalSummary(userId, summaryPayload);

      if (result?.summary) {
        setValue('summary', result.summary);
        clearErrors('summary');
      }
    } catch (error) {
      console.error('Error while generating AI description', error);
    } finally {
      setGenerationLoading(false);
    }
  };

  useEffect(() => {
    if (loading) return;

    if (category) {
      reset();

      if (data) {
        data.details?.forEach(() => append({}));
      }

      if (isCreation) {
        append({});
      }

      if (data && !isCreation) {
        initializeFormValues(
          data.details as Array<Record<string, unknown>>,
          data as Record<string, unknown>
        );
      }
    }
  }, [category, type, data, loading, isCreation]);

  useEffect(() => {
    const selectedSubType = schema[parsedType]?.subType?.find(
      (opt) => opt.value === data?.subType
    );

    if (selectedSubType) {
      setValueType(selectedSubType.valueType);
    } else {
      setValueType(schema[parsedType]?.valueType);
    }
  }, [data?.subType, type]);

  interface SelectOption {
    value: string;
    label: string;
  }

  const renderOptions = (
    options: Array<SubTypeOption | FunnelMetricOption>
  ): SelectOption[] =>
    options.map((option) => ({ value: option.value, label: option.label }));

  const getSelectedOptionValue = (
    options: Array<SubTypeOption | FunnelMetricOption>,
    value: unknown
  ): SelectOption | undefined => {
    const found = options.find((opt) => opt.value === value);

    return found ? { value: found.value, label: found.label } : undefined;
  };

  const handleSelectChange = (
    selectedOption: SelectOption,
    parsedTypeArg: string,
    schemaArg: typeof schema,
    onChange: (value: unknown) => void,
    setValueTypeArg: (v: string | undefined) => void
  ): void => {
    onChange(selectedOption.value);

    const newValueType = schemaArg[parsedTypeArg].subType?.find(
      (opt) => opt.value === selectedOption.value
    )?.valueType;

    setValueTypeArg(newValueType || schemaArg[parsedTypeArg].valueType);
  };

  const renderDetailFields = (): React.JSX.Element | null => {
    const isCombined = category === 'Combined';
    const isEditable = isCombined && (isEditing || isCreation);
    const parsedTypeLocal = type.replace(' ', '');

    const categoryConfig = schema[parsedTypeLocal]?.categories.find(
      (cat) => cat.name === category
    );

    if (!categoryConfig?.details) return null;

    return (
      <>
        {detailFields.map((item, detailIndex) => (
          <div
            key={item.id}
            className={classNames({
              'flex gap-3 justify-start items-start w-full': isCombined,
            })}
          >
            <DetailFields
              detailIndex={detailIndex}
              detailData={item as unknown as Record<string, unknown>}
              category={category}
              type={parsedTypeLocal}
              isDisabled={isDisabled}
            />
            {isEditable && detailFields.length > 1 && (
              <Button
                onClick={() => remove(detailIndex)}
                className="!h-auto !p-0"
              >
                <Icons.XinCircle className="text-navy-500 h-5 w-5 mt-14" />
              </Button>
            )}
          </div>
        ))}
        {isEditable && (
          <Button
            type="button"
            onClick={() => append({})}
            variant="reserve-outlined"
            iconAfter={<Icons.PlusInCircle />}
            size="sm"
            className="mt-3"
          >
            and
          </Button>
        )}
      </>
    );
  };

  return (
    <form>
      {category === 'Other' ? (
        <OtherGoalForm isDisabled={isDisabled} />
      ) : (
        <>
          <div className="flex gap-3 w-full flex-wrap">
            <Select
              label="Goal type"
              labelInTop
              value={{ label: type ?? '', value: type ?? '' }}
              options={[]}
              disabled
              selectWrapperClass="max-w-[150px]"
              data-testid="goal-type-select"
            />

            {schema[parsedType]?.funnelMetric && (
              <Controller
                name="funnelMetric"
                control={control}
                rules={{ required: true }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <Select
                    value={getSelectedOptionValue(
                      schema[parsedType].funnelMetric ?? [],
                      value
                    )}
                    onChange={(selectedOption) =>
                      onChange((selectedOption as SelectOption).value)
                    }
                    options={renderOptions(
                      schema[parsedType].funnelMetric ?? []
                    )}
                    disabled={isDisabled}
                    selectWrapperClass="max-w-[150px]"
                    labelInTop={true}
                    label="Funnel Metric"
                    fieldError={
                      error && { ...error, message: 'This field is required' }
                    }
                    isClearable={false}
                    clearInputErr={() => clearErrors('funnelMetric')}
                    required={true}
                    data-testid="goal-funnel-metric-select"
                  />
                )}
              />
            )}

            {schema[parsedType]?.subType && (
              <Controller
                name="subType"
                control={control}
                rules={{ required: true }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <Select
                    value={getSelectedOptionValue(
                      schema[parsedType].subType ?? [],
                      value
                    )}
                    onChange={(selectedOption) =>
                      handleSelectChange(
                        selectedOption as SelectOption,
                        parsedType,
                        schema,
                        onChange,
                        setValueType
                      )
                    }
                    options={renderOptions(schema[parsedType].subType ?? [])}
                    disabled={isDisabled}
                    selectWrapperClass="max-w-[150px]"
                    labelInTop={true}
                    label="Sub Type"
                    fieldError={
                      error && { ...error, message: 'This field is required' }
                    }
                    isClearable={false}
                    clearInputErr={() => clearErrors('subType')}
                    required={true}
                  />
                )}
              />
            )}

            {category !== 'Combined' && renderDetailFields()}

            <Controller
              name="value"
              control={control}
              rules={{
                validate: (value) => validateValue(value, valueType),
              }}
              render={({ field, fieldState: { error } }) => (
                <div>
                  <FormattedTextInput
                    {...field}
                    placeholder="Define..."
                    disabled={isDisabled}
                    inputWrapperCustomClass="max-w-[150px]"
                    label={getValueLabel(valueType)}
                    labelInTop={true}
                    disabledClassName="bg-gray-100"
                    errorsNames={error}
                    withSuffix={!TYPES_WITHOUT_SUFFIXES.includes(type)}
                    ref={null}
                    required={true}
                    data-testid="goal-value-input"
                  />
                </div>
              )}
            />
          </div>

          {category === 'Combined' && renderDetailFields()}

          <div className="mt-4 flex">
            <Controller
              name="relativeToLastYear"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <>
                  {radioOptions.map((option) => (
                    <RadioInput
                      key={option.value}
                      labelFor={option.value}
                      label={option.label}
                      radioValue={option.value}
                      labelClass={`${option.colorClass} font-semibold`}
                      setAction={field.onChange}
                      disabled={isDisabled}
                      {...field}
                      data-testid={getTestId({
                        prefix: 'goal-relative-to-last-year',
                        label: option.value,
                        suffix: 'radio',
                      })}
                    />
                  ))}
                </>
              )}
            />
          </div>

          <div className="my-4">
            <Controller
              name="summary"
              control={control}
              rules={{ required: 'This field is required' }}
              disabled={isDisabled || generationLoading}
              render={({ field, fieldState: { error } }) => (
                <GeneratingTextarea
                  {...field}
                  textareaWrapperStyles="max-w-[600px]"
                  label="AI Description"
                  rows={3}
                  loading={generationLoading}
                  helperText="The AI Description will be used on the Client's Goals page. It is editable."
                  errorsNames={error}
                  required={true}
                  data-testid="goal-summary-textarea"
                />
              )}
            />
            <div className="flex items-center gap-2 mt-3">
              <Button
                variant="reserve-outlined"
                onClick={generateSummary}
                disabled={isDisabled || generationLoading}
                size="sm"
                iconBefore={<Icons.Ai />}
                type="button"
                data-testid="goal-generate-description-button"
              >
                Generate description
              </Button>
            </div>
          </div>
        </>
      )}

      <div className="mt-5">
        <Controller
          name="note"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              textareaWrapperStyles="max-w-[600px]"
              label="Note"
              placeholder="Use this area to leave a note..."
              disabled={isDisabled}
              rows={3}
              data-testid="goal-note-textarea"
            />
          )}
        />
      </div>
    </form>
  );
};

export default React.memo(GoalForm);
